/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, March 31st 2024, 8:56:09 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { StyledOSDialog, OSActionButton, AlertSnackBar } from "ui-widgets";
import { TMediaAssetMetaData } from "lib/schemas";
import { useCopyTextToClipBoard } from "lib/utils";
import { MediaLibraryComponent } from "./MediaLibraryComponent";

type CopyFromMediaLibraryProps = {
  label?: string;
  successLabel?: string;
} & {
  buttonProps?: Pick<React.ComponentProps<typeof OSActionButton>, "variant">;
} & Pick<React.ComponentProps<typeof MediaLibraryComponent>, "onChooseAsset">;

export const CopyFromMediaLibrary = ({
  label,
  successLabel,
  buttonProps,
  onChooseAsset,
}: CopyFromMediaLibraryProps) => {
  const { copyTextToClipBoard } = useCopyTextToClipBoard();
  const [showCopiedAlert, setShowCopiedAlert] = React.useState(false);
  return (
    <>
      <AlertSnackBar
        open={showCopiedAlert}
        message={successLabel || "URL for asset copied to clipboard."}
        severity="success"
        vertical={"top"}
        horizontal={"center"}
        handleClose={() => setShowCopiedAlert(false)}
        canBeClosedByUser={false}
        autoClose
      />
      <StyledOSDialog
        popUpIcon={<ContentCopy />}
        popUpTitle={label || "Copy from Media Library"}
        maxWidth="xl"
        renderOpenButton={({ handleOpen }) => (
          <OSActionButton {...buttonProps} onClick={handleOpen}>
            {label || "Copy from Media Library"}
          </OSActionButton>
        )}
        renderContentWithClose={({ handleClose }) => {
          const onChooseAssetResolved = (metaData: TMediaAssetMetaData) => {
            if (onChooseAsset) {
              onChooseAsset(metaData);
            } else {
              copyTextToClipBoard(metaData.media_item_url);
            }
            setShowCopiedAlert(true);
            handleClose();
          };
          return <MediaLibraryComponent onChooseAsset={onChooseAssetResolved} />;
        }}
      />
    </>
  );
};
